import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Layers, Tag, FileText, Scale, Sparkles } from 'lucide-react';
import { z } from 'zod';
import { generateMaterialTypeCode } from '@/utils/entityCodeGenerator';
import { useConfigLookup } from '@/hooks/useConfigLookup';

export const materialTypeSchema = z.object({
  code: z.string().min(1, 'Vui lòng nhập mã loại vật tư').max(50, 'Vui lòng nhập dưới 50 ký tự (Mã loại vật tư không được vượt quá 50 ký tự)'),
  group: z.string().optional(),
  material: z.string().optional(),
  gsm: z.string().optional(),
  label: z.string().optional(),
});

export type MaterialTypeFormData = z.infer<typeof materialTypeSchema>;

export interface MaterialTypeFormProps {
  id?: string;
  isModal?: boolean;
  onSuccess?: (newItem: { id: string; label: string; code?: string; original?: any }) => void;
  onCancel?: () => void;
  initialData?: any;
  lockedFields?: Record<string, any>;
  submitLabel?: string;
}

export function MaterialTypeForm({
  id: propId,
  isModal = false,
  onSuccess,
  onCancel,
  initialData: propInitialData,
  lockedFields = {},
  submitLabel,
}: MaterialTypeFormProps) {
  const queryClient = useQueryClient();
  const isEdit = !!propId;

  // Load existing material types for dynamic suggestions
  const { data: existingTypes = [] } = useQuery({
    queryKey: ['config', 'materialTypes'],
    queryFn: () => trpcClient.config.getMaterialTypes.query(),
  });

  // Gợi ý lấy từ danh sách cài đặt (Nhóm vật tư, Loại phụ vật tư) + giá trị các loại vật tư đang dùng (FB-KWBVA8)
  const { options: groupListOptions } = useConfigLookup('material_groups', { valueField: 'name' });
  const { options: subtypeListOptions } = useConfigLookup('material_subtypes', { valueField: 'name' });

  const groupSuggestions = useMemo(() => {
    const groups = [
      ...groupListOptions.map((o) => o.label),
      ...(existingTypes as any[]).map((t: any) => t.group),
    ].filter(Boolean);
    return Array.from(new Set(groups)) as string[];
  }, [existingTypes, groupListOptions]);

  const materialSuggestions = useMemo(() => {
    const materials = [
      ...subtypeListOptions.map((o) => o.label),
      ...(existingTypes as any[]).map((t: any) => t.material),
    ].filter(Boolean);
    return Array.from(new Set(materials)) as string[];
  }, [existingTypes, subtypeListOptions]);

  const gsmSuggestions = useMemo(() => {
    const gsms = (existingTypes as any[]).map((t: any) => t.gsm).filter(Boolean);
    return Array.from(new Set(gsms)) as string[];
  }, [existingTypes]);

  const [formData, setFormData] = useState<MaterialTypeFormData>({
    code: propInitialData?.code || propInitialData?.name || '',
    label: propInitialData?.label || '',
    group: propInitialData?.group || lockedFields.group || '',
    material: propInitialData?.material || lockedFields.material || '',
    gsm: propInitialData?.gsm || lockedFields.gsm || '',
  });

  const isManualEditedRef = React.useRef(Boolean(propInitialData?.code));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleMaterialChange = (newMat: string) => {
    setFormData((prev) => {
      const updated = { ...prev, material: newMat };
      if (!isEdit && !isManualEditedRef.current) {
        updated.code = generateMaterialTypeCode({
          material: newMat,
          gsm: prev.gsm,
          group: prev.group,
        });
      }
      return updated;
    });
  };

  const handleGsmChange = (newGsm: string) => {
    setFormData((prev) => {
      const updated = { ...prev, gsm: newGsm };
      if (!isEdit && !isManualEditedRef.current) {
        updated.code = generateMaterialTypeCode({
          material: prev.material,
          gsm: newGsm,
          group: prev.group,
        });
      }
      return updated;
    });
  };

  const handleGroupChange = (newGroup: string) => {
    setFormData((prev) => {
      const updated = { ...prev, group: newGroup };
      if (!isEdit && !isManualEditedRef.current) {
        updated.code = generateMaterialTypeCode({
          material: prev.material,
          gsm: prev.gsm,
          group: newGroup,
        });
      }
      return updated;
    });
  };

  useEffect(() => {
    if (propInitialData) {
      isManualEditedRef.current = Boolean(propInitialData.code);
      setFormData({
        code: propInitialData.code || propInitialData.name || '',
        label: propInitialData.label || '',
        group: propInitialData.group || lockedFields.group || '',
        material: propInitialData.material || lockedFields.material || '',
        gsm: propInitialData.gsm || lockedFields.gsm || '',
      });
    }
  }, [propInitialData]);

  const saveMutation = useMutation({
    mutationFn: async (payload: MaterialTypeFormData) => {
      const computedLabel = payload.label?.trim() || (payload.material ? `${payload.material}${payload.gsm ? ` ${payload.gsm}gsm` : ''}` : payload.code.trim());

      if (isEdit && propId) {
        return await trpcClient.config.updateMaterialType.mutate({
          id: propId,
          data: {
            code: payload.code.trim(),
            group: payload.group?.trim() ?? '',
            material: payload.material?.trim() ?? '',
            gsm: payload.gsm?.trim() ?? '',
            label: computedLabel,
          },
        });
      }
      return await trpcClient.config.addMaterialType.mutate({
        code: payload.code.trim(),
        group: payload.group?.trim() || undefined,
        material: payload.material?.trim() || undefined,
        gsm: payload.gsm?.trim() || undefined,
        label: computedLabel,
      });
    },
    onSuccess: async (res: any) => {
      if (res) {
        queryClient.setQueryData(['material-types'], (old: any[] | undefined) => {
          const list = Array.isArray(old) ? old : [];
          return [res, ...list.filter((item: any) => item.id !== res.id)];
        });
      }

      toast.success(isEdit ? `Đã cập nhật loại vật tư thành công: ${formData.code}` : `Đã thêm loại vật tư: ${formData.code}`);

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['material-types'] }),
        queryClient.invalidateQueries({ queryKey: ['material-types'] }),
        queryClient.invalidateQueries({ queryKey: ['material-types-lookup'] }),
        queryClient.invalidateQueries({ queryKey: ['config', 'materialTypes'] }),
        queryClient.invalidateQueries({ queryKey: ['combobox'] }),
      ]);

      if (onSuccess && res) {
        onSuccess({
          id: res.id,
          label: res.code || res.label || formData.code,
          code: res.code || formData.code,
          original: res,
        });
      }
    },
    onError: (err: any) => {
      const rawMsg = err?.message || '';
      let cleanMsg = rawMsg;
      if (rawMsg.includes('Unique constraint failed') || rawMsg.includes('P2002') || rawMsg.includes('đã tồn tại')) {
        cleanMsg = `Mã loại vật tư '${formData.code}' đã tồn tại trong hệ thống. Vui lòng nhập mã khác.`;
      }
      toast.error(cleanMsg || `Lỗi ${isEdit ? 'cập nhật' : 'tạo'} loại vật tư`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const result = materialTypeSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0].toString()] = err.message;
        }
      });
      setErrors(fieldErrors);
      const firstErr = result.error.errors[0]?.message;
      if (firstErr) {
        toast.error(firstErr);
      }
      return;
    }

    // Kiểm tra trùng mã trên danh sách đang hoạt động trước khi gửi lên server
    const cachedList = (queryClient.getQueryData(['material-types']) as any[]) || [];
    const combinedList = [...cachedList, ...((existingTypes as any[]) || [])];
    const codeNorm = result.data.code.trim().toLowerCase();
    const isDuplicate = combinedList.some(
      (item: any) =>
        item &&
        !item.isDeleted &&
        String(item.code || '').trim().toLowerCase() === codeNorm &&
        (!isEdit || item.id !== propId)
    );
    if (isDuplicate) {
      const dupMsg = `Mã loại vật tư '${result.data.code.trim()}' đã tồn tại trong hệ thống. Vui lòng chọn hoặc nhập mã khác.`;
      setErrors({ code: dupMsg });
      toast.error(dupMsg);
      return;
    }

    setErrors({});
    saveMutation.mutate(result.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Code */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="mt-code" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Tag className="h-3.5 w-3.5 text-indigo-500" />
            Mã loại vật tư <span className="text-rose-500">*</span>
          </Label>
          <span
            className={`text-[10px] flex items-center gap-1 ${
              formData.code.length > 50 ? 'text-rose-600 font-bold' : 'text-slate-400'
            }`}
          >
            <Sparkles className="h-3 w-3 text-amber-500" /> {formData.code.length}/50 ký tự (tối đa 50 ký tự)
          </span>
        </div>
        <Input
          id="mt-code"
          autoFocus={!isEdit}
          placeholder="VD: IVO-300, D350, SE-2L..."
          value={formData.code}
          onChange={(e) => {
            const val = e.target.value.toUpperCase();
            isManualEditedRef.current = val.trim().length > 0;
            setFormData((prev) => ({ ...prev, code: val }));
            if (val.length > 50) {
              setErrors((prev) => ({
                ...prev,
                code: 'Vui lòng nhập dưới 50 ký tự (Mã loại vật tư không được vượt quá 50 ký tự)',
              }));
            } else if (errors.code) {
              setErrors((prev) => {
                const next = { ...prev };
                delete next.code;
                return next;
              });
            }
          }}
          className={`h-8 text-xs font-mono font-bold uppercase tracking-wider ${
            errors.code || formData.code.length > 50
              ? 'border-rose-500 focus-visible:ring-rose-500 text-rose-700'
              : 'focus-visible:ring-indigo-500'
          }`}
          disabled={lockedFields.code !== undefined}
        />
        {errors.code && (
          <p data-testid="mt-code-error" className="text-[11px] font-medium text-rose-600">
            {errors.code}
          </p>
        )}
      </div>

      {/* Tên / Nhãn hiển thị */}
      <div className="space-y-1.5">
        <Label htmlFor="mt-label" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-slate-500" />
          Tên / Nhãn hiển thị chuẩn
        </Label>
        <Input
          id="mt-label"
          placeholder="VD: Giấy Ivory định lượng 300gsm (tự động gợi ý nếu để trống)"
          value={formData.label}
          onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
          className="h-8 text-xs"
        />
      </div>

      {/* Grid: Nhóm & Chất liệu */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="mt-group" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5 text-slate-500" />
            Nhóm vật tư
          </Label>
          <Input
            id="mt-group"
            list="mt-group-list"
            placeholder="PAPER, INK, FILM, GLUE..."
            value={formData.group}
            onChange={(e) => handleGroupChange(e.target.value)}
            className="h-8 text-xs uppercase"
            disabled={lockedFields.group !== undefined}
          />
          <datalist id="mt-group-list">
            <option value="PAPER" />
            <option value="INK" />
            <option value="FILM" />
            <option value="GLUE" />
            <option value="CHEMICAL" />
            {groupSuggestions.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mt-material" className="text-xs font-semibold text-slate-700">
            Chất liệu chính
          </Label>
          <Input
            id="mt-material"
            list="mt-material-list"
            placeholder="VD: Ivory, Couches, Ford, Decal..."
            value={formData.material}
            onChange={(e) => handleMaterialChange(e.target.value)}
            className="h-8 text-xs"
            disabled={lockedFields.material !== undefined}
          />
          <datalist id="mt-material-list">
            {materialSuggestions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>
      </div>

      {/* Định lượng GSM */}
      <div className="space-y-1.5">
        <Label htmlFor="mt-gsm" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Scale className="h-3.5 w-3.5 text-slate-500" />
          Định lượng GSM
        </Label>
        <Input
          id="mt-gsm"
          list="mt-gsm-list"
          placeholder="VD: 300, 250, 150, 80..."
          value={formData.gsm}
          onChange={(e) => handleGsmChange(e.target.value)}
          className="h-8 text-xs"
          disabled={lockedFields.gsm !== undefined}
        />
        <datalist id="mt-gsm-list">
          <option value="80" />
          <option value="100" />
          <option value="120" />
          <option value="150" />
          <option value="200" />
          <option value="250" />
          <option value="300" />
          <option value="350" />
          <option value="400" />
          {gsmSuggestions.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={saveMutation.isPending}
            className="h-8 text-xs px-3"
          >
            Hủy
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={saveMutation.isPending}
          className="h-8 text-xs px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
        >
          {saveMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          {submitLabel || (isEdit ? 'Lưu thay đổi' : 'Tạo loại vật tư')}
        </Button>
      </div>
    </form>
  );
}
